import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const SECTIONS = [
  ["Fire, oxygen & burn risks", [
    ["smoking", "Does anyone smoke in the home?"],
    ["oxygen", "Is oxygen in use in the home?"],
    ["smoking_near_oxygen", "Any smoking or open flames near the oxygen?"],
    ["smoke_detectors", "Are smoke detectors missing or not working?"],
    ["co_detector", "Is the carbon monoxide detector missing or not working?"],
    ["exit_paths", "Are exit paths blocked or hard to use?"],
    ["space_heaters", "Space heaters in use, or cords / outlets overloaded?"],
    ["fire_extinguisher", "Is a fire extinguisher missing or out of reach?"],
    ["stove_left_on", "Stove left on, or cooking left unattended?"],
  ]],
  ["Fall risks", [
    ["rugs_cords", "Loose rugs or cords in the walkways?"],
    ["clutter_walkways", "Is clutter blocking walkways or stairs?"],
    ["stair_rails", "Are stair railings loose or missing?"],
    ["lighting", "Is the lighting poor (stairs, halls, bathroom)?"],
    ["bathroom", "Is the bathroom missing grab bars or non-slip surfaces?"],
  ]],
  ["Sanitation & environment", [
    ["pests", "Any signs of pests or infestation?"],
    ["odors", "Strong odors or unsanitary conditions?"],
    ["hoarding", "Hoarding or unsafe clutter?"],
    ["utilities", "Is heat, water, or electricity not working properly?"],
    ["food_spoilage", "Spoiled food, or refrigerator concerns?"],
  ]],
  ["People & animals", [
    ["pets_unruly", "Any unruly or aggressive pets?"],
    ["weapons", "Any unsecured weapons or firearms?"],
  ]],
  ["Client-specific risks", [
    ["wandering", "Wandering or exit-seeking behavior?"],
    ["meds_unsecured", "Are medications unsecured or within reach?"],
    ["phone_access", "Is a working phone out of reach, or emergency numbers not posted?"],
    ["water_temp", "Is the hot water scalding at the tap?"],
  ]],
];
const RATINGS = ["Not present", "Present — monitor", "Hazard — act now"];

export function HomeSafetyObservation({ clientId }) {
  const [evals, setEvals] = useState([]);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState({});
  const [notes, setNotes] = useState({});
  const [overall, setOverall] = useState("");
  const [actions, setActions] = useState("");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/home-safety-evals`).then((r) => setEvals(r.data.evals || [])).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const critical = (items.smoking_near_oxygen && items.smoking_near_oxygen !== "Not present")
    || (items.smoking && items.smoking !== "Not present" && items.oxygen && items.oxygen !== "Not present");

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/home-safety-evals`, { items, notes, overall_safe: overall, actions, other });
      toast.success("Home safety evaluation saved to the record");
      setOpen(false);
      setItems({}); setNotes({}); setOverall(""); setActions(""); setOther("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const ratingBtn = (key, r) => {
    const on = items[key] === r;  // blank default — untouched means "not observed", never an implied "No"
    return (
      <button key={r} type="button" onClick={() => setItems((m) => ({ ...m, [key]: r }))}
        className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
          on ? (r === "Hazard — act now" ? "border-[#C0392B] bg-[#C0392B] text-white" : r === "Present — monitor" ? "border-[#B7791F] bg-[#B7791F] text-white" : "border-[#2E7D32] bg-[#2E7D32] text-white")
             : "border-stone-300 bg-white text-stone-500 hover:border-stone-500"}`}
        data-testid={`hse-${key}-${r === "Not present" ? "ok" : r === "Present — monitor" ? "monitor" : "hazard"}`}>
        {r === "Not present" ? "No" : r === "Present — monitor" ? "Monitor" : "Hazard"}
      </button>
    );
  };

  return (
    <div className="mb-4 rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="home-safety-card">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Home safety evaluations <span className="font-normal text-stone-400">— what WE observed at the home</span></p>
          {evals.length > 0 && (
            <p className="text-[11px] text-stone-400">Last: {String(evals[0].at || "").slice(0, 10)} by {evals[0].by}{evals[0].critical ? " · ⚠ CRITICAL" : ""}</p>
          )}
        </div>
        <button onClick={() => setOpen((o) => !o)}
          className="inline-flex h-8 items-center rounded-full border border-stone-300 px-3 text-xs font-semibold text-stone-600 transition-colors hover:bg-stone-900 hover:text-white"
          data-testid="hse-new-btn">
          {open ? "Close" : "+ New evaluation"}
        </button>
      </div>

      {open && (
        <div className="border-t border-stone-100 px-5 py-4">
          {critical && (
            <p className="mb-3 rounded-lg border border-[#C0392B]/40 bg-[#C0392B]/10 px-4 py-2.5 text-xs font-bold text-[#C0392B]" data-testid="hse-critical-banner">
              ⚠ CRITICAL — smoking with oxygen present. Escalate to the office today; document what was said.
            </p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-stone-400">Mark only what you actually saw — anything left blank prints as <em>Not observed</em>, not "No".</p>
            <button type="button" onClick={() => setItems({})}
              className="text-[11px] font-medium text-stone-400 hover:text-stone-700" data-testid="hse-all-clear">
              Clear every answer
            </button>
          </div>
          {SECTIONS.map(([section, rows]) => (
            <div key={section} className="mb-4" data-testid={`hse-section-${section.replace(/\W+/g, "-").toLowerCase()}`}>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-stone-400">{section}</p>
              <div className="space-y-1.5">
                {rows.map(([key, label]) => (
                  <div key={key} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs text-stone-700">{label}</span>
                    <span className="flex gap-1">{RATINGS.map((r) => ratingBtn(key, r))}</span>
                  </div>
                ))}
              </div>
              <input value={notes[section] || ""} onChange={(e) => setNotes((m) => ({ ...m, [section]: e.target.value }))}
                placeholder="Notes for this section (optional)"
                className="mt-2 h-8 w-full rounded-lg border border-stone-200 px-3 text-xs" data-testid={`hse-notes-${section.replace(/\W+/g, "-").toLowerCase()}`} />
            </div>
          ))}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-stone-600">Safe to proceed with visits?</label>
              <select value={overall} onChange={(e) => setOverall(e.target.value)}
                className="h-9 w-full rounded-lg border border-stone-300 px-2 text-sm" data-testid="hse-overall">
                <option value="">— choose —</option>
                <option>Yes</option><option>Yes — with precautions (see actions)</option><option>No — hold visits until resolved</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-stone-600">Actions required</label>
              <input value={actions} onChange={(e) => setActions(e.target.value)}
                className="h-9 w-full rounded-lg border border-stone-300 px-3 text-sm" data-testid="hse-actions" />
            </div>
          </div>
          <input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Anything else observed…"
            className="mt-3 h-9 w-full rounded-lg border border-stone-300 px-3 text-sm" data-testid="hse-other" />
          <div className="mt-4 flex justify-end">
            <button onClick={save} disabled={busy}
              className="inline-flex h-9 items-center rounded-full bg-primary px-5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-stone-900 disabled:opacity-40"
              data-testid="hse-save">
              {busy ? "Saving…" : "Save evaluation"}
            </button>
          </div>
        </div>
      )}

      {evals.length > 0 && !open && (
        <div className="border-t border-stone-100 px-5 py-2">
          {evals.slice(0, 3).map((ev) => {
            const hazards = Object.values(ev.items || {}).filter((v) => v === "Hazard — act now").length;
            const monitors = Object.values(ev.items || {}).filter((v) => v === "Present — monitor").length;
            return (
              <div key={ev.id} className="flex items-center justify-between py-1.5 text-xs" data-testid={`hse-row-${ev.id}`}>
                <span className="text-stone-600">
                  {String(ev.at || "").slice(0, 10)} · {ev.by}
                  {ev.critical && <span className="ml-2 font-bold text-[#C0392B]">⚠ CRITICAL</span>}
                  {!ev.critical && (hazards > 0 || monitors > 0) && (
                    <span className="ml-2 text-stone-400">{hazards ? `${hazards} hazard${hazards > 1 ? "s" : ""}` : ""}{hazards && monitors ? " · " : ""}{monitors ? `${monitors} to monitor` : ""}</span>
                  )}
                  {!ev.critical && !hazards && !monitors && <span className="ml-2 text-[#2E7D32]">all clear</span>}
                </span>
                <a href={`${api.defaults.baseURL}/clients/${clientId}/home-safety-evals/${ev.id}.pdf`} target="_blank" rel="noreferrer"
                  className="font-medium text-[#6A3E97] hover:underline" data-testid={`hse-pdf-${ev.id}`}>View PDF</a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
